/**
 * BookmarksPanel - 북마크 관리 패널
 *
 * 현재 페이지 북마크 추가/삭제
 * 북마크 목록 보기 및 이동
 *
 * 홍익인간 (Hongik Ingan) - WIA Standards
 */

import { useState } from 'react';
import { useStore } from '../../store/useStore';
import { useI18n } from '../../i18n';
import { Bookmark } from '../../types';
import {
  FiBookmark,
  FiX,
  FiTrash2,
  FiClock,
  FiEdit3,
  FiCheck,
} from 'react-icons/fi';

interface BookmarksPanelProps {
  isOpen: boolean;
  onClose: () => void;
  bookId: string;
  fileType: 'epub' | 'pdf';
  currentPage?: number;
  currentCfi?: string;
  currentProgress: number;
  onNavigate: (bookmark: Bookmark) => void;
}

const BookmarksPanel = ({
  isOpen,
  onClose,
  bookId,
  fileType,
  currentPage,
  currentCfi,
  currentProgress,
  onNavigate,
}: BookmarksPanelProps) => {
  const { t } = useI18n();
  const { addBookmark, removeBookmark, getBookBookmarks, isPageBookmarked } = useStore();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');

  const bookBookmarks = getBookBookmarks(bookId).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const isCurrentPageBookmarked = isPageBookmarked(bookId, currentPage, currentCfi);

  // 현재 페이지 북마크 추가
  const handleAddBookmark = () => {
    const newBookmark: Bookmark = {
      id: `bookmark-${Date.now()}`,
      bookId,
      title: fileType === 'pdf'
        ? `${t('bookmarks.page') || '페이지'} ${currentPage}`
        : `${t('bookmarks.location') || '위치'} ${Math.round(currentProgress)}%`,
      pageNumber: fileType === 'pdf' ? currentPage : undefined,
      cfi: fileType === 'epub' ? currentCfi : undefined,
      progress: currentProgress,
      createdAt: new Date(),
    };
    addBookmark(newBookmark);
  };

  // 현재 페이지 북마크 삭제
  const handleRemoveCurrentBookmark = () => {
    const bookmark = bookBookmarks.find((b) => {
      if (fileType === 'pdf' && currentPage !== undefined) {
        return b.pageNumber === currentPage;
      }
      if (fileType === 'epub' && currentCfi) {
        return b.cfi === currentCfi;
      }
      return false;
    });
    if (bookmark) {
      removeBookmark(bookmark.id);
    }
  };

  // 북마크 제목 편집
  const handleEditTitle = (bookmark: Bookmark) => {
    setEditingId(bookmark.id);
    setEditTitle(bookmark.title);
  };

  const handleSaveTitle = (bookmarkId: string) => {
    // 현재는 store에 updateBookmark이 없으므로, 삭제 후 재추가로 처리
    const bookmark = bookBookmarks.find((b) => b.id === bookmarkId);
    if (bookmark) {
      removeBookmark(bookmarkId);
      addBookmark({ ...bookmark, title: editTitle });
    }
    setEditingId(null);
    setEditTitle('');
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-80 bg-white dark:bg-gray-800 shadow-2xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <FiBookmark className="text-primary" />
          {t('reader.bookmarks') || '북마크'}
        </h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <FiX size={20} className="text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* Add/Remove Current Bookmark */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        {isCurrentPageBookmarked ? (
          <button
            onClick={handleRemoveCurrentBookmark}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
          >
            <FiBookmark size={20} className="fill-current" />
            <span className="font-medium">{t('bookmarks.removeCurrent') || '현재 페이지 북마크 삭제'}</span>
          </button>
        ) : (
          <button
            onClick={handleAddBookmark}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
          >
            <FiBookmark size={20} />
            <span className="font-medium">{t('bookmarks.addCurrent') || '현재 페이지 북마크 추가'}</span>
          </button>
        )}
      </div>

      {/* Bookmarks List */}
      <div className="flex-1 overflow-y-auto">
        {bookBookmarks.length === 0 ? (
          <div className="p-8 text-center">
            <FiBookmark size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              {t('bookmarks.empty') || '아직 북마크가 없습니다'}
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
              {t('bookmarks.emptyHint') || '중요한 페이지를 북마크해보세요!'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {bookBookmarks.map((bookmark) => (
              <li
                key={bookmark.id}
                className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
              >
                <div className="flex items-start gap-3">
                  {/* Bookmark Icon with Progress */}
                  <div className="relative flex-shrink-0">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <FiBookmark size={20} className="text-primary" />
                    </div>
                    <span className="absolute -bottom-1 -right-1 text-xs bg-primary text-white px-1 rounded">
                      {Math.round(bookmark.progress)}%
                    </span>
                  </div>

                  {/* Bookmark Info */}
                  <div className="flex-1 min-w-0">
                    {editingId === bookmark.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveTitle(bookmark.id);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                        />
                        <button
                          onClick={() => handleSaveTitle(bookmark.id)}
                          className="p-1 text-green-500 hover:bg-green-100 dark:hover:bg-green-900/30 rounded"
                        >
                          <FiCheck size={16} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => onNavigate(bookmark)}
                        className="w-full text-left"
                      >
                        <h3 className="font-medium text-gray-900 dark:text-white truncate">
                          {bookmark.title}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                          <FiClock size={12} />
                          {formatDate(bookmark.createdAt)}
                        </p>
                      </button>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleEditTitle(bookmark)}
                      className="p-2 text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title={t('common.edit') || '편집'}
                    >
                      <FiEdit3 size={16} />
                    </button>
                    <button
                      onClick={() => removeBookmark(bookmark.id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                      title={t('common.delete') || '삭제'}
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          {t('bookmarks.total', { count: bookBookmarks.length }) || `총 ${bookBookmarks.length}개의 북마크`}
        </p>
      </div>
    </div>
  );
};

export default BookmarksPanel;
